import {
  faTicket,
  faChartLine,
  faShieldHalved,
  faBell,
  faWallet,
  faDice,
} from "@fortawesome/free-solid-svg-icons";
import { LANDING_FALLBACK } from "../views/pages/welcome/landingContent";
import { normalizeSiteFooter } from "../config/siteFooter";

const ICON_MAP = {
  ticket: faTicket,
  chart: faChartLine,
  shield: faShieldHalved,
  bell: faBell,
  wallet: faWallet,
  dice: faDice,
};

function resolveIcon(iconKey, existingIcon) {
  if (existingIcon) return existingIcon;
  return ICON_MAP[iconKey] || faTicket;
}

function pickHeroBg(h, data, fallback) {
  const overrides = data?.section_overrides?.hero || {};
  const apiDesktop =
    h.background_image_url ||
    h.background_image ||
    overrides.background_image_url ||
    overrides.background_image ||
    null;

  const apiMobile =
    h.background_image_mobile_url ||
    h.background_image_mobile ||
    h.background_image_url_mobile ||
    overrides.background_image_mobile_url ||
    overrides.background_image_mobile ||
    overrides.background_image_url_mobile ||
    null;

  const desktop = apiDesktop || fallback.background_image_url || null;
  // Si el CMS solo manda desktop, reutilizarlo en móvil (no mezclar con el
  // portrait local de Gold). El portrait local solo aplica sin imagen remota.
  const mobile =
    apiMobile ||
    (apiDesktop ? apiDesktop : fallback.background_image_mobile_url) ||
    desktop ||
    null;

  return { desktop, mobile };
}

function normalizeHero(data, fallback) {
  const h = data?.hero || {};
  const fb = fallback.hero;
  const { desktop: bgUrl, mobile: bgMobileUrl } = pickHeroBg(h, data, fb);

  // La API default suele traer títulos genéricos ("Gold Loterías") y pisa el
  // copy de marketing local. Solo usamos título/subtítulo remotos en campañas.
  const isCampaign =
    data?.page_type === "campaign" ||
    Boolean(data?.campaign_overlay) ||
    Boolean(data?.custom_title) ||
    Boolean(data?.custom_subtitle);

  return {
    background_image_url: bgUrl || null,
    background_image_mobile_url: bgMobileUrl || null,
    title: isCampaign
      ? h.title || data?.custom_title || data?.title || fb.title
      : fb.title,
    subtitle: isCampaign
      ? h.subtitle || data?.custom_subtitle || data?.subtitle || fb.subtitle
      : fb.subtitle,
    cta_text: h.cta_text || data?.cta_text || fb.cta_text,
    cta_link: h.cta_link || data?.cta_link || fb.cta_link,
    promo_image_url:
      data?.hero_image_url || data?.promoter_image || data?.image_url || null,
  };
}

function normalizeFeatures(data, fallback) {
  const sec = data?.features_section || data?.section_overrides?.features_section;
  const fb = fallback.features_section;
  if (!sec && !data?.features) return fb;

  const rawItems = sec?.items || data?.features || fb.items;
  const items = (Array.isArray(rawItems) ? rawItems : fb.items).map((item, i) => {
    const fbItem = fb.items[i] || fb.items.find((f) => f.sort_order === item.sort_order) || {};
    return {
      sort_order: item.sort_order ?? i + 1,
      title: item.title || fbItem.title || "",
      description: item.description || item.desc || fbItem.description || "",
      icon_key: item.icon_key || fbItem.icon_key || "ticket",
      icon: resolveIcon(item.icon_key || fbItem.icon_key, item.icon || fbItem.icon),
      image_url:
        item.image_url ||
        item.image ||
        fbItem.image_url ||
        null,
      image_alt: item.image_alt || item.title || fbItem.image_alt || "",
    };
  });

  return {
    title: sec?.title || fb.title,
    subtitle:
      sec && Object.prototype.hasOwnProperty.call(sec, "subtitle")
        ? sec.subtitle
        : fb.subtitle,
    items,
  };
}

function normalizeHowToPlay(data, fallback) {
  const sec =
    data?.how_to_play_section || data?.section_overrides?.how_to_play_section;
  const fb = fallback.how_to_play_section;
  if (!sec) return fb;

  const steps = (sec.steps || fb.steps).map((s) => ({
    step_number: s.step_number ?? s.n ?? 0,
    title: s.title || "",
    description: s.description || s.desc || "",
  }));

  return {
    title: sec.title || fb.title,
    subtitle: Object.prototype.hasOwnProperty.call(sec, "subtitle")
      ? sec.subtitle
      : fb.subtitle,
    steps,
  };
}

function normalizeConversion(data, fallback) {
  const sec =
    data?.conversion_section || data?.section_overrides?.conversion_section;
  const fb = fallback.conversion_section;
  const c = sec || {};

  return {
    form_title: c.form_title || fb.form_title,
    form_subtitle: c.form_subtitle || fb.form_subtitle,
    cta_label: c.cta_label || fb.cta_label,
    contact_title: c.contact_title || fb.contact_title,
    mobile_column_title: c.mobile_column_title || fb.mobile_column_title,
    qr_image_url: c.qr_image_url || c.qr_image || fb.qr_image_url,
    play_store_url:
      c.play_store_url ||
      data?.play_store_link ||
      data?.campaign_overlay?.play_store_link ||
      fb.play_store_url,
    app_store_url:
      c.app_store_url ||
      data?.app_store_link ||
      data?.campaign_overlay?.app_store_link ||
      fb.app_store_url,
    login_link_text: c.login_link_text || fb.login_link_text,
  };
}

function normalizeLegalLotteries(data, fallback) {
  const sec =
    data?.legal_lotteries_section ||
    data?.section_overrides?.legal_lotteries_section;
  const fb = fallback.legal_lotteries_section;
  if (!sec) return fb;

  const rawItems = sec.items || [];
  const items = Array.isArray(rawItems)
    ? rawItems
        .map((item, i) => ({
          name: item.name || item.title || "",
          logo_url: item.logo_url || item.image_url || item.image || null,
          lottery_id: item.lottery_id || item.id || null,
          sort_order: item.sort_order ?? i + 1,
        }))
        .filter((item) => item.name || item.logo_url)
    : [];

  return {
    title: sec.title || fb.title,
    subtitle: Object.prototype.hasOwnProperty.call(sec, "subtitle")
      ? sec.subtitle
      : fb.subtitle,
    items: items.length > 0 ? items : fb.items,
  };
}

function normalizeFooter(data, fallback) {
  const sec = data?.footer_section || data?.section_overrides?.footer_section;
  const fb = fallback.footer_section;
  if (!sec) return normalizeSiteFooter(fb);

  return normalizeSiteFooter({
    company_description: sec.company_description || fb.company_description,
    columns: sec.columns?.length ? sec.columns : fb.columns,
    payment_logos: sec.payment_logos?.length
      ? sec.payment_logos
      : fb.payment_logos,
    regulatory_logos: sec.regulatory_logos?.length
      ? sec.regulatory_logos
      : fb.regulatory_logos,
    social_links: sec.social_links || fb.social_links,
  });
}

function normalizeCampaignOverlay(data, slugFromRoute) {
  if (!data) return null;

  const overlay = data.campaign_overlay || data;
  const slug = data.slug || slugFromRoute || null;
  if (!slug && data.page_type !== "campaign") return null;

  return {
    slug,
    promoter_name: overlay.promoter_name || null,
    referral_code:
      overlay.referral_code ||
      overlay.campaign_code ||
      overlay.promo_code_value ||
      slug,
    campaign_code: overlay.campaign_code || null,
    custom_title: overlay.custom_title || overlay.title || null,
    custom_subtitle:
      overlay.custom_subtitle || overlay.subtitle || overlay.promo_terms || null,
    hero_image_url:
      overlay.hero_image_url || overlay.promoter_image || overlay.image_url || null,
    cta_text: overlay.cta_text || null,
    cta_link: overlay.cta_link || null,
    play_store_link: overlay.play_store_link || overlay.play_store_url || null,
    app_store_link: overlay.app_store_link || overlay.app_store_url || null,
    is_active: overlay.is_active !== false,
  };
}

/**
 * Fusiona payload API con fallback local.
 * @param {object|null} defaultData
 * @param {object|null} campaignData
 * @param {string|undefined} slugFromRoute
 */
export function mergeLandingContent(defaultData, campaignData, slugFromRoute) {
  const fallback = LANDING_FALLBACK;
  const base = campaignData?.override_sections
    ? campaignData
    : { ...defaultData, ...campaignData };

  const merged = {
    is_mock: Boolean(
      (defaultData?.is_mock || campaignData?.is_mock) &&
        !defaultData?.hero?.title &&
        !campaignData?.hero?.title
    ),
    page_type: slugFromRoute ? "campaign" : "default",
    slug: slugFromRoute || null,
    is_active: base?.is_active !== false,
  };

  const hero = normalizeHero(
    {
      ...defaultData,
      ...campaignData,
      hero: campaignData?.section_overrides?.hero ||
        campaignData?.hero ||
        defaultData?.hero,
      custom_title: campaignData?.campaign_overlay?.custom_title,
      custom_subtitle: campaignData?.campaign_overlay?.custom_subtitle,
      hero_image_url: campaignData?.campaign_overlay?.hero_image_url,
      cta_text: campaignData?.campaign_overlay?.cta_text,
      cta_link: campaignData?.campaign_overlay?.cta_link,
    },
    fallback
  );

  const useCampaignSections = Boolean(campaignData?.override_sections);

  const contentSource = useCampaignSections && campaignData
    ? campaignData
    : { ...defaultData, ...campaignData };

  return {
    ...merged,
    hero,
    features_section: normalizeFeatures(contentSource, fallback),
    how_to_play_section: normalizeHowToPlay(contentSource, fallback),
    conversion_section: normalizeConversion(
      { ...defaultData, ...campaignData },
      fallback
    ),
    legal_lotteries_section: normalizeLegalLotteries(contentSource, fallback),
    footer_section: normalizeFooter(contentSource, fallback),
    campaign_overlay: normalizeCampaignOverlay(campaignData, slugFromRoute),
  };
}

/**
 * @param {object|null} apiData — respuesta única (default o slug)
 * @param {string|undefined} slugFromRoute
 */
export function normalizeLandingContent(apiData, slugFromRoute) {
  if (!apiData || typeof apiData !== "object") {
    return mergeLandingContent(null, null, slugFromRoute);
  }

  if (apiData.page_type === "campaign" || slugFromRoute) {
    return mergeLandingContent(null, apiData, slugFromRoute);
  }

  return mergeLandingContent(apiData, null, slugFromRoute);
}
